import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import UserService from "services/UserService";
import { getActionPlansList } from "services/ConfigServices";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import RTPlanActions from "components/RTPlanActions/RTPlanActions";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import PlanActionsCard from "components/PlanActions/PlanActionsCard.js";
import PlanActionsCard2 from "components/PlanActions/PlanActionsCard2";
import CustomCardCharts from "components/CustomCardCharts/CustomCardCharts";
import useActionTable from "./useActionTable.js";
import {
  AutocompleteInput,
  CustomSelect,
  CustomSwitch,
  DateInput,
  FloatInput,
  IntInput,
  LocalizationAutocomplete,
  TextInput,
} from "components/Input/Index.js";

export default function ActionsAnalysis() {
  const { actions_dashboard } = useActionTable();

  const {
    totalactions,
    newactions,
    finished,
    creation,
    creation_late,
    standby,
    standby_late,
    inprogress,
    inprogress_late,
    pending,
    pending_late,
    evolution,
  } = actions_dashboard;

  const optionsActionsEvolution = {
    chart: {
      type: "area",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "Total actions",
      },
    },
    xAxis: {
      type: "datetime",
    },
    tooltip: {
      crosshairs: true,
      shared: true,
      valueSuffix: " actions",
      xDateFormat: "%A, %b %e",
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    plotOptions: {
      area: {
        stacking: "normal",
        lineColor: "#666666",
        lineWidth: 1,
        marker: {
          enabled: false,
          lineWidth: 1,
          lineColor: "#666666",
        },
      },
    },
    series: [
      {
        name: "En cours",
        data: inprogress_evolutions(evolution || []),
      },
      {
        name: "En retard",
        data: late_evolutions(evolution || []),
      },
      {
        name: "Terminées",
        data: finished_evolutions(evolution || []),
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };

  const optionsActions = {
    chart: {
      type: "column",
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: [
        "Création",
        "Approuvées",
        "En cours",
        "En pause",
        "Terminées",
      ],
    },
    yAxis: {
      min: 0,
      title: {
        text: "",
      },
      stackLabels: {
        enabled: false,
        style: {
          fontWeight: "bold",
          color:
            // theme
            (Highcharts.defaultOptions.title.style &&
              Highcharts.defaultOptions.title.style.color) ||
            "gray",
        },
      },
    },
    legend: {
      align: "right",
      x: -30,
      verticalAlign: "top",
      y: 25,
      floating: false,
      backgroundColor:
        Highcharts.defaultOptions.legend.backgroundColor || "white",
      borderColor: "#CCC",
      borderWidth: 1,
      shadow: false,
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y}",
    },
    plotOptions: {
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: [
      {
        name: "Total",
        data: notLate(),
      },
      {
        name: "En retard",
        data: late(),
      },
    ],
  };

  function subtractArray(arr1, arr2) {
    return arr2.map(function (el, i) {
      return Math.abs(el - arr1[i]);
    });
  }

  function notLate() {
    let data = [creation, standby, inprogress, pending];
    let dataLate = [creation_late, standby_late, inprogress_late, pending_late];

    return [...subtractArray(dataLate, data), finished];
  }
  function late() {
    let dataLate = [creation_late, standby_late, inprogress_late, pending_late];

    return dataLate;
  }

  function inprogress_evolutions(data) {
    let projectedArray = [];
    data.map(([date, inprogress, late, finished]) =>
      projectedArray.push([date, inprogress])
    );

    return projectedArray;
  }
  function late_evolutions(data) {
    let projectedArray = [];
    data.map(([date, inprogress, late, finished]) =>
      projectedArray.push([date, late])
    );

    return projectedArray;
  }
  function finished_evolutions(data) {
    let projectedArray = [];
    data.map(([date, inprogress, late, finished]) =>
      projectedArray.push([date, finished])
    );

    return projectedArray;
  }
  return (
    <>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
          <CustomCardCharts
            cardTitle="Total des actions"
            cardNumber={totalactions}
            dataTitle="Statut des actions (dont en retard)"
            dataName0="Nouveaux (ce mois-ci)"
            data0result={newactions}
            dataName1="Création"
            data1result={`${creation} (${creation_late})`}
            dataName2="Approuvées"
            data2result={`${standby} (${standby_late})`}
            dataName3="En cours"
            data3result={`${inprogress} (${inprogress_late})`}
            dataName4="En pause"
            data4result={`${pending} (${pending_late})`}
            dataName5="Terminées"
            data5result={finished}
            graphicName={""}
            graphic={
              <HighchartsReact
                containerProps={{
                  style: { height: "14.3rem", width: "100%" },
                }}
                highcharts={Highcharts}
                options={optionsActions}
              />
            }
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={12} lg={6} xl={6}>
          <CustomCardCharts
            graphicName={""}
            graphic={
              <HighchartsReact
                containerProps={{
                  style: { height: "24.7rem", width: "100%" },
                }}
                highcharts={Highcharts}
                options={optionsActionsEvolution}
              />
            }
          />
        </GridItem>
      </GridContainer>
    </>
  );
}
